import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { FlightSegment } from '@hotelplan/graphql.types';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { formatDate, parseDateStr } from '@hotelplan/libs.utils';

const DATE_FORMAT = 'EEE dd.MM.yyyy';
const WEEKDAY_FORMAT = 'EEE';

const ChangedFlightItemWrap = styled.div(
  sx2CssThemeFn({
    mx: [2, 4],
    borderBottom: '1px solid #c2c2c2',
    py: [2, 3],
    '.departure-date': {
      display: 'grid',
      gridTemplateColumns: '100px 1fr auto',
      alignItems: 'baseline',
      mb: 2,
      '.root-title': {
        fontSize: [0, 1],
      },
      h3: {
        color: 'secondary',
        fontSize: [1, '22px'],
      },
    },
    '.time-wrap': {
      fontSize: 0,
      color: 'textGray',
      display: 'grid',
      gridTemplateColumns: '100px 1fr auto',
      '&:not(:last-child)': {
        mb: 2,
      },
      '.time-wrap-title': {
        fontWeight: 'bold',
      },
    },
    '.flight-details': {
      display: 'flex',
      gap: [2, 3],
      alignItems: 'start',
      fontSize: 0,
      color: 'textGray',
      img: {
        maxHeight: '40px',
      },
      '.text': {
        lineHeight: '1.3',
      },
    },
    '.departure-airport': {
      mt: 3,
    },
    '.total-flight-time': {
      color: 'secondary',
      fontSize: [0, 1],
    },
  })
);

export interface IFlightSectionProps {
  segments?: FlightSegment[];
  departureAirport?: string;
  departureAirportPlaceholder: string;
  flightDuration: string;
  stopLocations: string[];
  arrivalAirport?: string;
  arrivalAirportPlaceholder: string;
  departureDate: Date;
  className?: string;
  isReturnFlight?: boolean;
  airlineLogo?: string;
  departureTime?: string;
  arrivalTime?: string;
}

function FlightItem(props: IFlightSectionProps) {
  const [t, { language }] = useTranslation(['common', 'checkout']);

  const formatOfferDate = useCallback(
    (date: Date) => formatDate(date, DATE_FORMAT, language),
    [language]
  );

  const formatOfferWeekday = useCallback(
    (date: string) => formatDate(parseDateStr(date), WEEKDAY_FORMAT, language),
    [language]
  );

  const {
    segments,
    departureDate,
    arrivalAirportPlaceholder,
    departureAirportPlaceholder,
    flightDuration,
    isReturnFlight,
  } = props;

  return (
    <ChangedFlightItemWrap>
      <div className="departure-date">
        <h3>
          {isReturnFlight
            ? t('common:returnFlight.title')
            : t('common:offer.departureDate')}
        </h3>
        <h2 className="root-title">
          {formatOfferDate(departureDate)} {t('common:flight.from')}{' '}
          {departureAirportPlaceholder} {t('common:flight.to')}{' '}
          {arrivalAirportPlaceholder}
        </h2>
      </div>

      {segments?.map((segment, index) => {
        return (
          <React.Fragment key={`segment-${index}`}>
            <div className="time-wrap">
              <div>
                <div className="time-wrap-title">
                  {t('common:offer.departureDate')}
                </div>
                <div>
                  {formatOfferWeekday(segment.departureDate)}{' '}
                  {segment.departureTime}
                </div>
              </div>

              <div className="departure-airport">
                {segment.departure.name} ({segment.departure.iata})
              </div>
              <div className="flight-details">
                <img
                  src={segment.airline.airlineLogo}
                  alt={segment.airline.name}
                />
                <div className="text">
                  <div>{segment.airline.name}</div>
                  <div>{segment.flightNumber}</div>
                  <div>{segment.segmentDuration}</div>
                </div>
              </div>
            </div>
            <div className="time-wrap">
              <div>
                <div className="time-wrap-title">
                  {t('common:offer.arrivalDate')}
                </div>
                <div>
                  {formatOfferWeekday(segment.arrivalDate)}{' '}
                  {segment.arrivalTime}
                </div>
              </div>

              <div className="departure-airport">
                {segment.arrival.name} ({segment.arrival.iata})
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <h3 className="total-flight-time">
        {t('checkout:totalFlightTime.title')} {flightDuration}
      </h3>
    </ChangedFlightItemWrap>
  );
}

export default FlightItem;
